import { useState } from 'react';
import { Button, Card, Input, Select, Switch, Badge, FileUpload } from '@dreamtree-org/twreact-ui';
import { Navigation, Phone, MapPin, LogIn, Camera, CloudOff, Cloud, RefreshCw, CheckCircle2, Circle, Hand, NotebookPen } from 'lucide-react';
import { PageHeader, QueryState, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { useAppApi } from '@/lib/api';
import { isValidOtp } from '../domain';
import { useAction, ActionResult, formatTime, formatDateTime, OTP_HINT, HandoverCode } from '../components';
import { uploadFile } from '@/lib/uploads';

let draftSeq = 1;
const draftId = () => `draft-${Date.now().toString(36)}-${draftSeq++}`;

function Step({ done, label }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {done ? <CheckCircle2 size={16} className="text-primary-600" aria-hidden /> : <Circle size={16} className="opacity-40" aria-hidden />}
      <span className={done ? '' : 'opacity-70'}>{label}</span>
    </li>
  );
}

function VisitCard({ visit, pending, submit, technicians, assign }) {
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState('');
  const [otp, setOtp] = useState('');
  const [condition, setCondition] = useState('');
  const isPickup = visit.kind === 'pickup';
  const mine = pending.filter((d) => d.visitId === visit.id);
  const checkedIn = visit.status !== 'scheduled' || mine.some((d) => d.kind === 'checkIn');
  const hasEvidence = visit.evidence.length > 0 || mine.some((d) => d.kind === 'evidence');
  const done = visit.status === 'done' || mine.some((d) => d.kind === 'ack');
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(visit.location.address)}`;
  const tel = `tel:${visit.customer.phone.replace(/[^\d+]/g, '')}`;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-bold tabular-nums">{formatTime(visit.scheduledAt)}</span>
            <Badge variant={isPickup ? 'primary' : 'success'} size="sm">
              {isPickup ? 'Pickup' : 'Delivery'}
            </Badge>
            {done ? (
              <Badge variant="success" size="sm">
                Done
              </Badge>
            ) : null}
            {mine.length ? (
              <Badge variant="warning" size="sm">
                {mine.length} draft{mine.length === 1 ? '' : 's'}
              </Badge>
            ) : null}
          </div>
          <div className="font-semibold mt-1">{visit.customer.name}</div>
          <div className="text-sm opacity-80 flex items-start gap-1">
            <MapPin size={14} className="mt-0.5 shrink-0" aria-hidden /> {visit.location.address}
          </div>
          <div className="text-sm opacity-70">
            {visit.device.model} · {visit.problem} · slot {visit.slot}
          </div>
        </div>
      </div>
      {!visit.technicianId && visit.status !== 'done' ? (
        <Select
          label="Assign an agent"
          options={technicians.map((t) => ({ value: t.id, label: `${t.name} · ${t.zone}` }))}
          value=""
          onChange={(v) => v && assign(visit, v)}
          closeOnSelect
          placeholder="Unassigned: pick a technician"
        />
      ) : (
        <p className="text-xs opacity-70">Agent: {visit.technicianName} · {visit.id}</p>
      )}

      <div className="grid grid-cols-2 gap-2">
        <a href={mapUrl} target="_blank" rel="noreferrer" className="min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md border border-[var(--app-border)] text-sm font-medium hover:bg-[var(--app-bg)]">
          <Navigation size={16} aria-hidden /> Navigate
        </a>
        <a href={tel} className="min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md border border-[var(--app-border)] text-sm font-medium hover:bg-[var(--app-bg)]">
          <Phone size={16} aria-hidden /> Call customer
        </a>
      </div>

      <ul className="space-y-1">
        <Step done={checkedIn} label={checkedIn && visit.checkInAt ? `Checked in ${formatTime(visit.checkInAt)}` : 'Check in at the location'} />
        <Step done={hasEvidence} label={`Evidence photos (${visit.evidence.length + mine.filter((d) => d.kind === 'evidence').reduce((s, d) => s + d.params.files.length, 0)})`} />
        <Step done={done} label={isPickup ? 'Customer hands over the device (OTP)' : 'Customer receives the device (OTP)'} />
      </ul>

      {!done ? (
        <div className="space-y-3">
          {!checkedIn ? (
            <Button fullWidth size="lg" onClick={() => submit(visit, 'checkIn', 'serviceFieldCheckIn', { id: visit.id }, 'Checked in')} leftIcon={<LogIn size={18} />}>
              Check in
            </Button>
          ) : null}

          <div className="space-y-2">
            <div className="text-sm font-medium flex items-center gap-1">
              <Camera size={16} aria-hidden /> Evidence capture
            </div>
            <FileUpload accept="image/*" multiple files={files} onFileSelect={(f) => setFiles(f)} onFileRemove={(i) => setFiles((fs) => fs.filter((_, idx) => idx !== i))} maxSize={10 * 1024 * 1024} />
            <Button
              variant="outline"
              fullWidth
              disabled={!files.length}
              onClick={() => {
                submit(visit, 'evidence', 'serviceFieldEvidence', { id: visit.id, files }, `${files.length} photo(s)`);
                setFiles([]);
              }}
            >
              Save {files.length || ''} photo{files.length === 1 ? '' : 's'}
            </Button>
          </div>

          {checkedIn ? (
            <div className="space-y-2 rounded-md border border-[var(--app-border)] p-3">
              <div className="text-sm font-medium flex items-center gap-1">
                <Hand size={16} aria-hidden /> {isPickup ? 'Pickup acknowledgement' : 'Delivery acknowledgement'}
              </div>
              <Input label="Condition" value={condition} onChange={(e) => setCondition(e.target.value)} placeholder={isPickup ? 'Scuffs, dents, charger included…' : 'Working, demonstrated to customer'} />
              <HandoverCode purpose={isPickup ? 'custody' : 'return'} id={isPickup ? visit.ticketId : visit.jobId} />
              <Input label="Customer’s code" type="tel" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} helperText={OTP_HINT} />
              <Button
                fullWidth
                size="lg"
                disabled={!isValidOtp(otp) || !hasEvidence}
                onClick={() => submit(visit, 'ack', 'serviceFieldAck', { id: visit.id, otp, condition }, isPickup ? 'Pickup acknowledged' : 'Delivery acknowledged')}
              >
                {isPickup ? 'Confirm pickup' : 'Confirm delivery'}
              </Button>
              {!hasEvidence ? <p className="text-xs opacity-70">Capture at least one photo first.</p> : null}
            </div>
          ) : null}
        </div>
      ) : visit.ack ? (
        <p className="text-sm opacity-80">
          Acknowledged {formatDateTime(visit.ack.at)} · {visit.ack.ref}
        </p>
      ) : null}

      <div className="space-y-2 border-t border-[var(--app-border)] pt-3">
        <div className="text-sm font-medium flex items-center gap-1">
          <NotebookPen size={16} aria-hidden /> Worklog
        </div>
        {visit.worklog.map((w) => (
          <p key={w.id} className="text-sm">
            <span className="opacity-70 tabular-nums mr-2">{formatTime(w.at)}</span>
            {w.note}
          </p>
        ))}
        {mine
          .filter((d) => d.kind === 'worklog')
          .map((d) => (
            <p key={d.params.clientDraftId} className="text-sm opacity-70 italic">
              {d.params.note} (draft)
            </p>
          ))}
        <div className="flex gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Traffic, gate closed, customer late…" aria-label="Worklog note" className="flex-1" />
          <Button
            variant="outline"
            disabled={!note.trim()}
            onClick={() => {
              submit(visit, 'worklog', 'serviceFieldWorklog', { id: visit.id, note }, 'Note');
              setNote('');
            }}
          >
            Add
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * SERV-04 Technician field jobs (R-06), phone-first. "Offline" is simulated:
 * actions become drafts with a client id and sync when back online; the server
 * (fixture) ignores a draft id it has already applied, so a retry never doubles.
 */
export default function FieldJobs() {
  const [tech, setTech] = useState('');
  const [offline, setOffline] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState('');
  const q = useQuery('serviceFieldJobs', { technicianId: tech || undefined }, { select: (r) => r.data });

  // Evidence photos are uploaded right before their action is sent (a draft keeps the files).
  const withUploads = async (params, recordId) => {
    const { files, ...body } = params;
    if (!files) return body;
    const mediaIds = [];
    for (const f of files) mediaIds.push((await uploadFile(f, { ownerType: 'field_visit', ownerId: recordId, kind: 'evidence' })).media_id);
    return { ...body, mediaIds };
  };
  const assign = async (visit, technicianId) => {
    const res = await act.run('serviceFieldAssign', { id: visit.id, technicianId }, `${visit.id} assigned.`);
    if (res) q.reload();
  };
  const techs = useQuery('serviceTechnicians', null, { select: (r) => r.data });
  const { fetchApi } = useAppApi();
  const act = useAction();

  const submit = async (visit, kind, apiKey, params, label) => {
    const body = { ...params, clientDraftId: draftId() };
    if (offline) {
      setDrafts((d) => [...d, { visitId: visit.id, recordId: visit.recordId, kind, apiKey, params: body, label }]);
      return;
    }
    let ready;
    try {
      ready = await withUploads(body, visit.recordId);
    } catch (ex) {
      setSyncNote(`${label}: upload failed (${ex.message}).`);
      return;
    }
    const res = await act.run(apiKey, ready, `${label}: saved.`);
    if (res) q.reload();
  };

  const sync = async () => {
    setSyncing(true);
    let applied = 0;
    let dupes = 0;
    const failed = [];
    for (const d of drafts) {
      try {
        const res = await fetchApi(d.apiKey, await withUploads(d.params, d.recordId), false);
        if (res && res.duplicate) dupes += 1;
        else applied += 1;
      } catch (ex) {
        failed.push(`${d.label}: ${(ex.response && ex.response.data && ex.response.data.error) || ex.message}`);
      }
    }
    setDrafts([]);
    setSyncing(false);
    setSyncNote(`Synced ${applied} draft${applied === 1 ? '' : 's'}${dupes ? `, ${dupes} duplicate${dupes === 1 ? '' : 's'} ignored` : ''}${failed.length ? `. Rejected: ${failed.join('; ')}` : '.'}`);
    q.reload();
  };

  const goOnline = (on) => {
    setOffline(!on);
    setSyncNote('');
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <PageHeader title="My jobs today" subtitle={new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })} />

      <Card className="p-3 space-y-3">
        <Select
          label="Technician"
          options={[{ value: '', label: 'All technicians' }, { value: 'unassigned', label: 'Unassigned visits' }, ...(techs.data || []).map((t) => ({ value: t.id, label: t.name }))]}
          value={tech}
          onChange={(v) => setTech(v || '')}
          closeOnSelect
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Switch checked={!offline} onChange={goOnline} rightLabel={offline ? 'Offline (simulated)' : 'Online'} ariaLabel="Network connection (simulated)" />
          <span className="flex items-center gap-2 text-sm">
            {offline ? <CloudOff size={16} aria-hidden /> : <Cloud size={16} aria-hidden />}
            {drafts.length ? (
              <Badge variant="warning" size="sm">
                {drafts.length} draft{drafts.length === 1 ? '' : 's'} not synced
              </Badge>
            ) : (
              <Badge variant="success" size="sm">
                All synced
              </Badge>
            )}
          </span>
        </div>
        {!offline && drafts.length ? (
          <Button fullWidth onClick={sync} loading={syncing} leftIcon={<RefreshCw size={16} />}>
            Sync {drafts.length} draft{drafts.length === 1 ? '' : 's'}
          </Button>
        ) : null}
        {offline ? <p className="text-xs opacity-70">Offline: actions are saved on this phone as drafts and sent when you are back online. Each draft has an id, so a retried sync is never applied twice.</p> : null}
        {syncNote ? <p className="text-sm">{syncNote}</p> : null}
      </Card>

      <ActionResult action={act} />
      <QueryState query={q}>
        {(visits) =>
          visits.length === 0 ? (
            <EmptyState title="No field jobs">Nothing is scheduled for this technician.</EmptyState>
          ) : (
            <div className="space-y-3">
              {visits.map((v) => (
                <VisitCard key={v.id} visit={v} pending={drafts} submit={submit} technicians={techs.data || []} assign={assign} />
              ))}
            </div>
          )
        }
      </QueryState>
    </div>
  );
}
